import { useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { type ColumnDef } from '@tanstack/react-table';
import {
  Boxes,
  Check,
  ChevronDown,
  CircleDollarSign,
  Layers,
  ListFilter,
  PackageCheck,
  Plus,
} from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { MetricCard } from '@/components/financials/MetricCard';
import { Card, CardContent } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { DataTable, arrIncludesSome, type FacetedFilter } from '@/components/ui/DataTable';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import { NewBomDialog } from '@/components/manufacturing/NewBomDialog';
import { useItemKinds, useItems, type Item } from '@/features/inventory/hooks';
import {
  BOM_TYPES,
  BOM_TYPE_LABEL,
  bomTypesForItem,
  canEditBoms,
  hasBomLines,
  makeCostResolver,
  subAssemblyKindSlugs,
  useBomGraph,
  type BomCategory,
  type BomRecord,
  type BomType,
  type RolledCost,
} from '@/features/manufacturing/boms';
import { useAuth } from '@/app/AuthProvider';
import { formatCurrency, formatDate } from '@/lib/formatters';
import { cn } from '@/lib/utils';

const RENEW_PREFIX = 'FG-RNW';

const TYPE_PARAM: Record<BomType, string> = {
  finished_good: 'fg',
  sub_assembly: 'sub',
  component: 'comp',
};

const TYPE_SHORT: Record<BomType, string> = {
  finished_good: 'FG',
  sub_assembly: 'Sub-assy',
  component: 'Component',
};

/** No `types` param means every type; an empty one means none. */
function parseTypes(raw: string | null): Set<BomType> {
  if (raw === null) return new Set(BOM_TYPES.map((t) => t.value));
  const wanted = new Set(raw.split(','));
  return new Set(BOM_TYPES.filter((t) => wanted.has(TYPE_PARAM[t.value])).map((t) => t.value));
}

interface BomRow {
  item: Item;
  types: BomType[];
  bom: BomRecord | null;
  lines: number;
  subAssemblies: number;
  usedIn: Array<{ id: string; sku: string; name: string }>;
  cost: RolledCost | null;
  bomStatus: 'Has BOM' | 'Empty BOM' | 'No BOM';
}

function CostCell({ cost }: { cost: RolledCost | null }) {
  if (!cost) return <span className="block text-right text-muted-foreground">—</span>;
  return (
    <span className="block text-right tabular-nums">
      {formatCurrency(Math.round(cost.cents))}
      {cost.missing > 0 && (
        <span className="block text-[11px] text-fresh-salmon-dark">
          {cost.missing} part{cost.missing === 1 ? '' : 's'} w/o cost
        </span>
      )}
    </span>
  );
}

function TypeToggle({
  label,
  count,
  active,
  onToggle,
}: {
  label: string;
  count: number;
  active: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={active}
      className={cn(
        'inline-flex items-center gap-1.5 whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium transition-all',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
        active
          ? 'bg-card text-info shadow-[0_1px_2px_0_hsl(124_26%_14%/0.08)]'
          : 'text-muted-foreground hover:text-foreground',
      )}
    >
      <span
        className={cn(
          'flex h-4 w-4 items-center justify-center rounded border',
          active ? 'border-info bg-info text-info-foreground' : 'border-border',
        )}
      >
        {active && <Check className="h-3 w-3" />}
      </span>
      {label}
      <span className="rounded-full bg-secondary/80 px-1.5 text-[10px] font-medium tabular-nums text-muted-foreground">
        {count}
      </span>
    </button>
  );
}

/**
 * Bills of materials across every BOM level. Finished goods, sub-assemblies,
 * and components are toggled independently (all on by default); an item
 * that is several types is listed once and tagged with each. Rows open the
 * multi-level builder at /mfg/boms/:itemId.
 */
export function BomsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { role } = useAuth();
  const canEdit = canEditBoms(role);
  const [params, setParams] = useSearchParams();
  const selected = useMemo(() => parseTypes(params.get('types')), [params]);
  const [newOpen, setNewOpen] = useState(false);
  const [newCategory, setNewCategory] = useState<BomCategory>('finished_good');

  const { data: items, isLoading: itemsLoading } = useItems();
  const { data: graph, isLoading: graphLoading, error: graphError } = useBomGraph();
  const { data: kinds, isLoading: kindsLoading } = useItemKinds();
  const isLoading = itemsLoading || graphLoading || kindsLoading;

  const cost = useMemo(() => makeCostResolver(graph), [graph]);
  const subAssemblyKinds = useMemo(() => subAssemblyKindSlugs(kinds), [kinds]);

  const rows: BomRow[] = useMemo(() => {
    const out: BomRow[] = [];
    for (const item of items ?? []) {
      if (item.status === 'archived') continue;
      const types = bomTypesForItem(item, graph, subAssemblyKinds);
      if (types.size === 0) continue;
      const bom = graph?.byItemId.get(item.id) ?? null;
      const lines = bom?.lines.length ?? 0;
      out.push({
        item,
        types: BOM_TYPES.map((t) => t.value).filter((t) => types.has(t)),
        bom,
        lines,
        subAssemblies:
          bom?.lines.filter((l) => hasBomLines(graph, l.component_item_id)).length ?? 0,
        usedIn: [...(graph?.usedIn.get(item.id) ?? [])]
          .map((id) => graph?.byItemId.get(id)?.item)
          .filter((i): i is NonNullable<typeof i> => !!i)
          .map((i) => ({ id: i.id, sku: i.sku, name: i.name }))
          .sort((a, b) => a.sku.localeCompare(b.sku)),
        cost: lines > 0 ? cost(item.id) : null,
        bomStatus: !bom ? 'No BOM' : lines === 0 ? 'Empty BOM' : 'Has BOM',
      });
    }
    return out;
  }, [items, graph, subAssemblyKinds, cost]);

  const visibleRows = useMemo(
    () => rows.filter((r) => r.types.some((t) => selected.has(t))),
    [rows, selected],
  );

  const metrics = useMemo(() => {
    const counts: Record<BomType, number> = { finished_good: 0, sub_assembly: 0, component: 0 };
    for (const r of rows) for (const t of r.types) counts[t] += 1;
    const finishedGoods = rows.filter((r) => r.types.includes('finished_good'));
    const renew = finishedGoods.filter((r) =>
      (r.item.parent_sku ?? r.item.sku).startsWith(RENEW_PREFIX),
    );
    const parts = new Set<string>();
    for (const b of graph?.boms ?? []) for (const l of b.lines) parts.add(l.component_item_id);
    return {
      counts,
      renewTotal: renew.length,
      renewWithBom: renew.filter((r) => r.lines > 0).length,
      fgWithBom: finishedGoods.filter((r) => r.lines > 0).length,
      fgTotal: finishedGoods.length,
      parts: parts.size,
    };
  }, [rows, graph]);

  const columns: ColumnDef<BomRow>[] = useMemo(
    () => [
      {
        id: 'sku',
        accessorFn: (r) => r.item.sku,
        header: 'SKU',
        size: 170,
        cell: ({ row }) => <span className="font-mono text-xs">{row.original.item.sku}</span>,
      },
      {
        id: 'name',
        accessorFn: (r) => r.item.name,
        header: 'Item',
        size: 260,
        cell: ({ row }) => <span className="font-medium">{row.original.item.name}</span>,
      },
      {
        id: 'type',
        accessorFn: (r) => r.types.map((t) => BOM_TYPE_LABEL[t]).join(', '),
        header: 'Type',
        size: 200,
        cell: ({ row }) => (
          <span
            className="flex flex-wrap gap-1"
            title={row.original.types.map((t) => BOM_TYPE_LABEL[t]).join(', ')}
          >
            {row.original.types.map((t) => (
              <Badge
                key={t}
                variant="outline"
                className="whitespace-nowrap px-1.5 py-0 text-[11px] font-normal"
              >
                {TYPE_SHORT[t]}
              </Badge>
            ))}
          </span>
        ),
      },
      {
        id: 'bomStatus',
        accessorFn: (r) => r.bomStatus,
        header: 'BOM',
        size: 110,
        filterFn: arrIncludesSome,
        cell: ({ row }) => {
          const r = row.original;
          if (r.bomStatus === 'No BOM') return <Badge variant="warning-soft">No BOM</Badge>;
          if (r.bomStatus === 'Empty BOM') return <Badge variant="muted">Empty</Badge>;
          return (
            <Badge variant="success-soft">
              v{r.bom?.version?.version ?? 1}
              {r.bom?.version?.is_active ? ' · active' : ''}
            </Badge>
          );
        },
      },
      {
        id: 'lines',
        accessorFn: (r) => r.lines,
        header: () => <span className="block text-right">Lines</span>,
        size: 64,
        cell: ({ row }) => (
          <span className="block text-right tabular-nums">{row.original.lines || '—'}</span>
        ),
      },
      {
        id: 'subs',
        accessorFn: (r) => r.subAssemblies,
        header: () => <span className="block text-right">Sub-assy</span>,
        size: 80,
        cell: ({ row }) => (
          <span className="block text-right tabular-nums">{row.original.subAssemblies || '—'}</span>
        ),
      },
      {
        id: 'usedIn',
        accessorFn: (r) => r.usedIn.map((u) => `${u.sku} ${u.name}`).join(' '),
        header: 'Used in',
        size: 200,
        cell: ({ row }) => {
          const used = row.original.usedIn;
          if (used.length === 0) return <span className="text-muted-foreground">—</span>;
          return (
            <span className="text-sm" title={used.map((u) => u.sku).join('\n')}>
              {used.length} assembl{used.length === 1 ? 'y' : 'ies'}
              <span className="ml-1 text-xs text-muted-foreground">
                ·{' '}
                {used
                  .slice(0, 2)
                  .map((u) => u.sku)
                  .join(', ')}
                {used.length > 2 ? '…' : ''}
              </span>
            </span>
          );
        },
      },
      {
        id: 'group',
        accessorFn: (r) => (r.item.model_filters ?? []).join(', ') || '—',
        getUniqueValues: (r) => r.item.model_filters ?? [],
        filterFn: (row, _id, value: string[] | undefined) =>
          !value?.length || (row.original.item.model_filters ?? []).some((g) => value.includes(g)),
        header: 'Group',
        size: 130,
      },
      {
        id: 'color',
        accessorFn: (r) => r.item.color ?? '—',
        header: 'Color',
        size: 84,
        filterFn: arrIncludesSome,
      },
      {
        id: 'region',
        accessorFn: (r) => r.item.region ?? '—',
        header: 'Region',
        size: 80,
        filterFn: arrIncludesSome,
      },
      {
        id: 'std',
        accessorFn: (r) => r.item.std_cost_cents ?? -1,
        header: () => <span className="block text-right">Std cost</span>,
        size: 90,
        cell: ({ row }) => (
          <span className="block text-right tabular-nums text-muted-foreground">
            {formatCurrency(row.original.item.std_cost_cents)}
          </span>
        ),
      },
      {
        id: 'cost',
        accessorFn: (r) => r.cost?.cents ?? -1,
        header: () => <span className="block text-right">BOM cost</span>,
        size: 110,
        cell: ({ row }) => <CostCell cost={row.original.cost} />,
      },
      {
        id: 'updated',
        accessorFn: (r) => r.bom?.updated_at ?? '',
        header: 'BOM updated',
        size: 110,
        cell: ({ row }) => (
          <span className="text-muted-foreground">
            {row.original.bom ? formatDate(row.original.bom.updated_at) : '—'}
          </span>
        ),
      },
    ],
    [],
  );

  const filters: FacetedFilter[] = useMemo(() => {
    const opts = (vals: string[]) => [...new Set(vals)].sort().map((v) => ({ value: v, label: v }));
    return [
      {
        columnId: 'bomStatus',
        title: 'BOM',
        options: opts(visibleRows.map((r) => r.bomStatus)),
      },
      {
        columnId: 'group',
        title: 'Group',
        options: opts(visibleRows.flatMap((r) => r.item.model_filters ?? [])),
      },
      {
        columnId: 'color',
        title: 'Color',
        options: opts(visibleRows.map((r) => r.item.color ?? '—')),
      },
      {
        columnId: 'region',
        title: 'Region',
        options: opts(visibleRows.map((r) => r.item.region ?? '—')),
      },
    ];
  }, [visibleRows]);

  const setTypes = (next: Set<BomType>) => {
    const p = new URLSearchParams(params);
    p.delete('tab');
    if (next.size === BOM_TYPES.length) p.delete('types');
    else
      p.set(
        'types',
        BOM_TYPES.filter((t) => next.has(t.value))
          .map((t) => TYPE_PARAM[t.value])
          .join(','),
      );
    setParams(p, { replace: true });
  };

  const toggleType = (type: BomType) => {
    const next = new Set(selected);
    if (next.has(type)) next.delete(type);
    else next.add(type);
    setTypes(next);
  };

  const startNew = (category: BomCategory) => {
    setNewCategory(category);
    setNewOpen(true);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Manufacturing"
        title="Bills of materials"
        actions={
          canEdit ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button>
                  <Plus className="h-4 w-4" />
                  New BOM
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={() => startNew('finished_good')}>
                  <Boxes className="h-4 w-4" />
                  Finished-good BOM
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => startNew('sub_assembly')}>
                  <Layers className="h-4 w-4" />
                  Sub-assembly
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : undefined
        }
      />

      {graphError && (
        <p className="rounded-md border border-fresh-salmon/60 bg-fresh-salmon/15 px-4 py-3 text-sm text-fresh-salmon-dark">
          Couldn’t load BOMs: {graphError instanceof Error ? graphError.message : 'unknown error'}.
          If this mentions <code>position</code>, migration 114 hasn’t been applied yet.
        </p>
      )}

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <MetricCard
          label="Renew toilets with a BOM"
          value={`${metrics.renewWithBom} / ${metrics.renewTotal}`}
          icon={<PackageCheck className="h-4 w-4" />}
          loading={isLoading}
        />
        <MetricCard
          label="Finished goods with a BOM"
          value={`${metrics.fgWithBom} / ${metrics.fgTotal}`}
          icon={<Boxes className="h-4 w-4" />}
          loading={isLoading}
        />
        <MetricCard
          label="Sub-assemblies"
          value={metrics.counts.sub_assembly.toLocaleString()}
          icon={<Layers className="h-4 w-4" />}
          loading={isLoading}
        />
        <MetricCard
          label="Distinct parts referenced"
          value={metrics.parts.toLocaleString()}
          icon={<CircleDollarSign className="h-4 w-4" />}
          loading={isLoading}
        />
      </div>

      <div className="space-y-4">
        <div
          role="group"
          aria-label="Item types"
          className="inline-flex min-h-10 flex-wrap items-center gap-1 rounded-lg bg-secondary p-1 text-muted-foreground"
        >
          {BOM_TYPES.map((t) => (
            <TypeToggle
              key={t.value}
              label={t.label}
              count={metrics.counts[t.value]}
              active={selected.has(t.value)}
              onToggle={() => toggleType(t.value)}
            />
          ))}
        </div>

        <Card>
          <CardContent className="pt-6">
            <DataTable<BomRow, unknown>
              data={visibleRows}
              columns={columns}
              isLoading={isLoading}
              getRowId={(r) => r.item.id}
              onRowClick={(r) =>
                navigate(`/mfg/boms/${r.item.id}`, {
                  state: { from: `${location.pathname}${location.search}` },
                })
              }
              searchPlaceholder="Search by SKU, name, or type…"
              facetedFilters={filters}
              initialPageSize={50}
              pageSizes={[25, 50, 100]}
              tableId="mfg-boms"
              enableColumnResizing
              initialColumnVisibility={{ color: false, region: false, updated: false }}
              emptyState={
                selected.size === 0 ? (
                  <EmptyState
                    icon={ListFilter}
                    title="No types selected"
                    action={
                      <Button
                        variant="outline"
                        onClick={() => setTypes(new Set(BOM_TYPES.map((t) => t.value)))}
                      >
                        Show all types
                      </Button>
                    }
                  />
                ) : (
                  <EmptyState icon={Boxes} title="No matching items" />
                )
              }
              mobileCard={{
                title: (r) => r.item.name,
                subtitle: (r) => <span className="font-mono">{r.item.sku}</span>,
                badges: (r) => [
                  ...r.types.map((t) => (
                    <Badge key={t} variant="outline">
                      {BOM_TYPE_LABEL[t]}
                    </Badge>
                  )),
                  <Badge
                    key="bom"
                    variant={r.bomStatus === 'Has BOM' ? 'success-soft' : 'warning-soft'}
                  >
                    {r.bomStatus}
                  </Badge>,
                ],
                meta: (r) => [
                  { label: 'Lines', value: r.lines || '—' },
                  { label: 'Used in', value: r.usedIn.length || '—' },
                  {
                    label: 'BOM cost',
                    value: r.cost ? formatCurrency(Math.round(r.cost.cents)) : '—',
                  },
                ],
              }}
            />
          </CardContent>
        </Card>
      </div>

      {canEdit && <NewBomDialog open={newOpen} onOpenChange={setNewOpen} category={newCategory} />}
    </div>
  );
}
